/**
 * Collection Dock Item Component
 *
 * Displays a single item thumbnail in the Collection Dock.
 * Shows preview image for media, or styled card for articles.
 */

import { Component, input, output, computed, inject, ChangeDetectionStrategy } from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faXmark,
  faFile,
  faImage,
  faVideo,
  faMusic,
  faFileAlt,
  faFolder,
  faFileLines,
  faNewspaper
} from '@fortawesome/pro-duotone-svg-icons';

import { CollectionItem } from '../../models/collection.models';
import { Item } from '../../models/item';
import { LocaleService } from '../../services/locale.service';
import { servingBody } from '../../utils/article-body-state';
import { AppConfigService } from '../../services/app-config.service';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { escapeHtml } from '../../utils/escape-html';

@Component({
  selector: 'app-collection-dock-item',
  standalone: true,
  imports: [
    TranslateModule,
    TooltipModule,
    FontAwesomeModule,
    SafeHtmlPipe
  ],
  template: `
    <div
      class="dock-item"
      [class.article]="isArticle"
      [pTooltip]="itemTitle"
      tooltipPosition="top">

      <!-- Thumbnail (for media items) -->
      @if (thumbnailUrl) {
        <img [src]="thumbnailUrl" [alt]="itemTitle" loading="lazy" />
        <!-- Type indicator badge -->
        <div class="type-badge">
          <fa-duotone-icon [icon]="itemIcon"></fa-duotone-icon>
        </div>
      } @else if (isArticle) {
        <!-- Article miniature: scaled-down rendered HTML. The inert attribute keeps the body's links
             out of the Tab order and off the pointer — a decorative copy never takes focus or
             navigates away (the article card's miniature and the dashboard's featured-articles tile
             carry the same attribute; one invariant, three homes, rostered in this item's spec). -->
        @if (articleHtml()) {
          <div class="article-miniature">
            <div class="article-miniature-content" inert [innerHTML]="articleHtml() | safeHtml"></div>
          </div>
        } @else {
          <div class="article-card">
            <div class="article-icon">
              <fa-duotone-icon [icon]="faNewspaper"></fa-duotone-icon>
            </div>
            <div class="article-title">{{ truncatedTitle }}</div>
          </div>
        }
      } @else {
        <!-- Generic placeholder for other items without thumbnails -->
        <div class="item-placeholder">
          <fa-duotone-icon [icon]="itemIcon"></fa-duotone-icon>
        </div>
      }

      <!-- Remove button: always in the DOM when removable, so the keyboard reaches it; the
           stylesheet reveals it on hover and on focus-within alike. -->
      @if (canRemove()) {
        <button
          class="remove-btn"
          (click)="onRemove($event)"
          [pTooltip]="'common.remove' | translate"
          tooltipPosition="top">
          <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
        </button>
      }
    </div>
  `,
  styles: [`
    .dock-item {
      width: 64px;
      height: 64px;
      border-radius: 4px;
      overflow: hidden;
      position: relative;
      flex-shrink: 0;
      border: 1px solid var(--p-surface-border);
      background: var(--p-surface-100);
      cursor: pointer;
      transition: border-color 0.15s ease, transform 0.15s ease;
      user-select: none;

      &:hover {
        border-color: var(--p-surface-300);
        transform: translateY(-2px);
      }

      // The remove button reveals on hover AND on focus-within — a hover-only reveal left the
      // keyboard no way to reach it (it was not even in the DOM until the pointer arrived).
      &:hover .remove-btn,
      &:focus-within .remove-btn {
        opacity: 1;
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      &.article {
        background: linear-gradient(135deg, var(--p-surface-100) 0%, var(--p-surface-200) 100%);
        border-color: var(--p-surface-200);

        &:hover {
          border-color: var(--p-primary-200);
        }
      }
    }

    .item-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--p-surface-100);

      fa-duotone-icon {
        font-size: 1.25rem;
        color: var(--p-text-muted-color);
      }
    }

    .article-miniature {
      width: 100%;
      height: 100%;
      overflow: hidden;
      position: relative;
      pointer-events: none;

      .article-miniature-content {
        width: 540px;
        padding: 16px 24px;
        transform: scale(0.119);
        transform-origin: top left;
        font-size: 14px;
        line-height: 1.6;
        color: var(--p-text-color);

        h1, h2, h3, h4, h5, h6 {
          font-weight: 600;
          margin: 0 0 6px 0;
        }

        h1 { font-size: 20px; }
        h2 { font-size: 17px; }
        h3 { font-size: 15px; }

        p {
          margin: 0 0 8px 0;
        }

        img {
          max-width: 100%;
          height: auto;
        }
      }
    }

    .article-card {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      padding: 6px;

      .article-icon {
        fa-duotone-icon {
          font-size: 0.875rem;
          color: var(--p-primary-color);
          opacity: 0.7;
        }
      }

      .article-title {
        flex: 1;
        font-size: 0.5625rem;
        font-weight: 500;
        line-height: 1.3;
        color: var(--p-text-color);
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        word-break: break-word;
        margin-top: 2px;
      }
    }

    .type-badge {
      position: absolute;
      bottom: 3px;
      left: 3px;
      width: 16px;
      height: 16px;
      border-radius: 3px;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      align-items: center;
      justify-content: center;

      fa-duotone-icon {
        font-size: 0.5rem;
        color: white;
      }
    }

    .remove-btn {
      position: absolute;
      top: 2px;
      right: 2px;
      width: 18px;
      height: 18px;
      border: none;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: background-color 0.15s ease, opacity 0.15s ease;

      fa-duotone-icon {
        font-size: 0.5rem;
        color: white;
      }

      &:hover {
        background: rgba(244, 67, 54, 0.9);
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollectionDockItemComponent {
  readonly collectionItem = input.required<CollectionItem>();
  readonly canRemove = input(false);
  readonly remove = output<string>();

  // Icons
  faXmark = faXmark;
  faFile = faFile;
  faImage = faImage;
  faVideo = faVideo;
  faMusic = faMusic;
  faFileAlt = faFileAlt;
  faFolder = faFolder;
  faFileLines = faFileLines;
  faNewspaper = faNewspaper;

  private localeService = inject(LocaleService);
  private configService = inject(AppConfigService);
  private translate = inject(TranslateService);

  // Article miniature — built from text already on the collection item: `<h1>title</h1>` + the body in the
  // lens, or — where the body exists only in another language — the two-state sentence in the body's
  // position (the miniature is an EXCERPT slot, ruled 2026-09-14: never the foreign body, never a blank
  // miniature under a headline that names the language); empty when no body exists in any language.
  articleHtml = computed(() => {
    const item = this.item;
    if (!item || item.typeName !== 'Article' || !item.text) return '';
    // The headline is the one editorial read label (the two-state word when the lens has none); the
    // body reads the serving language — the lens's own or a same-language variant, never a foreign body.
    const title = this.localeService.editorialLabel(item);
    const text = servingBody(item.text as Record<string, string>, this.localeService.activeContentLanguage());
    const sentence = text ? null : this.localeService.untranslatedBodySentence(item.text as Record<string, string>);
    // The title (and the sentence) is plain text interpolated into markup — escaped through the encoder the
    // article card's miniature shares, so "A < B" renders literally in both homes (the body is the author's
    // rich HTML, re-sanitized by the safeHtml pipe at render).
    const body = text || (sentence ? `<p>${escapeHtml(sentence)}</p>` : '');
    if (!body) return '';
    return (title ? `<h1>${escapeHtml(title)}</h1>` : '') + body;
  });

  get item(): Item | undefined {
    return this.collectionItem()?.item;
  }

  get isArticle(): boolean {
    return this.item?.typeName === 'Article';
  }

  get thumbnailUrl(): string | null {
    if (!this.item) return null;

    // Articles don't have thumbnails
    if (this.isArticle) return null;

    // Use small preview for dock items
    const previewUrl = this.item.fileData?.previews?.small?.url;
    if (previewUrl) {
      return this.configService.getStorageUrl(previewUrl);
    }

    return null;
  }

  /** The one editorial read label (the title in the content lens, else the name — a file's original
   *  filename stands in for a name it lacks — else the two-state untitled word). */
  get itemTitle(): string {
    if (!this.item) return '';
    return this.localeService.editorialLabel({
      title: this.item.title,
      name: this.item.name || this.item.fileData?.originalFilename || null
    });
  }

  get truncatedTitle(): string {
    const title = this.itemTitle;
    // For the small card, we let CSS handle truncation with line-clamp
    return title;
  }

  get itemIcon() {
    if (!this.item) return this.faFile;

    switch (this.item.typeName) {
      case 'Image':
        return this.faImage;
      case 'Video':
        return this.faVideo;
      case 'Audio':
        return this.faMusic;
      case 'Document':
        return this.faFileAlt;
      case 'Article':
        return this.faFileLines;
      case 'Folder':
      case 'Gallery':
        return this.faFolder;
      default:
        return this.faFile;
    }
  }

  onRemove(event: Event): void {
    event.stopPropagation();
    this.remove.emit(this.collectionItem().itemId);
  }
}
